import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { Device } from '@/types/device';
import { FilterCategory } from '@/components/PersonFilter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const DEVICE_ID_KEY = 'team_v_device_id';
const DEVICE_CACHE_KEY = 'team_v_device_cache';

export type DevicePrefsCache = {
  showCompleted?: boolean;
  showSnoozed?: boolean;
  filterPersonIds?: string[];
  filterCategory?: FilterCategory;
  activeTab?: 'tasks' | 'groceries' | 'stock' | 'meals' | 'health' | 'done' | 'fyp' | 'states' | 'goals' | 'voice' | 'agents';
  tvViewMode?: Device['tvViewMode'];
  enableAnimations?: boolean;
  animationStyle?: Device['animationStyle'];
  timeFilterDays?: number | null;
};

export const getCachedDevicePrefs = (): DevicePrefsCache | null => {
  try {
    const raw = localStorage.getItem(DEVICE_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DevicePrefsCache;
  } catch {
    return null;
  }
};

export const setCachedDevicePrefs = (updates: Partial<DevicePrefsCache>) => {
  try {
    const current = getCachedDevicePrefs() ?? {};
    const next: DevicePrefsCache = { ...current, ...updates };
    localStorage.setItem(DEVICE_CACHE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
};

const saveDevicePrefsCache = (device: Device) => {
  const current = getCachedDevicePrefs();
  const payload: DevicePrefsCache = {
    showCompleted: device.showCompleted,
    showSnoozed: device.showSnoozed,
    filterPersonIds: device.filterPersonIds,
    filterCategory: device.filterCategory,
    activeTab: current?.activeTab,
    tvViewMode: device.tvViewMode,
    enableAnimations: device.enableAnimations,
    animationStyle: device.animationStyle,
    timeFilterDays: device.timeFilterDays,
  };
  try {
    localStorage.setItem(DEVICE_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

// IP address fetching removed for privacy (GDPR compliance)

// Explicit column allowlist for `devices` reads — kept in sync with `mapToDevice`
// below. Replacing `.select('*')` with `.select(DEVICE_COLUMNS)` prevents future
// internal columns added to the table (e.g. push tokens, auth fingerprints,
// billing/device-trust flags, audit fields) from auto-flowing to the client and
// from being exfiltrated via a stolen session.
const DEVICE_COLUMNS = [
  'id',
  'created_at',
  'last_seen_at',
  'name',
  'user_agent',
  'default_view',
  'is_tv_mode',
  'ask_who_completed',
  'last_ip_address',
  'show_completed',
  'show_snoozed',
  'filter_person_ids',
  'filter_category',
  'tv_view_mode',
  'enable_animations',
  'animation_style',
  'time_filter_days',
  'active_tab',
].join(', ');

// Map database row to Device type
const mapToDevice = (row: any): Device => ({
  id: row.id,
  createdAt: new Date(row.created_at),
  lastSeenAt: new Date(row.last_seen_at),
  name: row.name,
  userAgent: row.user_agent,
  defaultView: row.default_view || 'normal',
  isTvMode: row.is_tv_mode || false,
  askWhoCompleted: row.ask_who_completed ?? true,
  lastIpAddress: row.last_ip_address ?? null,
  // Persistent preferences
  showCompleted: row.show_completed ?? false,
  showSnoozed: row.show_snoozed ?? false,
  filterPersonIds: row.filter_person_ids ?? [],
  filterCategory: (row.filter_category as FilterCategory) ?? 'any',
  tvViewMode: row.tv_view_mode ?? 'date',
  enableAnimations: row.enable_animations ?? false,
  animationStyle: row.animation_style ?? 'line',
  timeFilterDays: row.time_filter_days !== undefined ? row.time_filter_days : 7,
  activeTab: row.active_tab ?? null,
});

// Helper to construct initial device state from cache
const getInitialDeviceFromCache = (deviceId: string): Device | null => {
  const cachedPrefs = getCachedDevicePrefs();
  if (!cachedPrefs) return null;
  return {
    id: deviceId,
    createdAt: new Date(),
    lastSeenAt: new Date(),
    name: null,
    userAgent: null,
    defaultView: 'normal',
    isTvMode: false,
    askWhoCompleted: true,
    lastIpAddress: null,
    showCompleted: cachedPrefs.showCompleted ?? false,
    showSnoozed: cachedPrefs.showSnoozed ?? false,
    filterPersonIds: cachedPrefs.filterPersonIds ?? [],
    filterCategory: cachedPrefs.filterCategory ?? 'any',
    tvViewMode: cachedPrefs.tvViewMode ?? 'date',
    enableAnimations: cachedPrefs.enableAnimations ?? false,
    animationStyle: cachedPrefs.animationStyle ?? 'line',
    timeFilterDays: cachedPrefs.timeFilterDays !== undefined ? cachedPrefs.timeFilterDays : 7,
    activeTab: cachedPrefs.activeTab ?? null,
  };
};

export function useDevice() {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  // 1. Get or Create Device ID
  const { data: deviceId, isLoading: isDeviceIdLoading } = useQuery({
    queryKey: ['deviceId'],
    queryFn: async () => {
      let id = localStorage.getItem(DEVICE_ID_KEY);
      if (!id) {
        // Generate UUID on the client to avoid `.select()` which fails for anon users due to RLS
        // Fallback since crypto.randomUUID() is not available in non-secure contexts (HTTP IP access)
        const generateUUID = () => {
          if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
          }
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        };
        const newId = generateUUID();
        const { error } = await supabase
          .from('devices')
          .insert({
            id: newId,
            user_agent: navigator.userAgent,
            user_id: user?.id || null,
          });

        if (error) {
          // If 401, check if we're trying to insert with a user_id we shouldn't have, or if anon insert is blocked
          console.error('[useDevice] Failed to register device. This might be due to RLS or Auth state.', {
            error,
            status: error.code,
            message: error.message
          });
          throw error;
        }

        id = newId;
        localStorage.setItem(DEVICE_ID_KEY, id!);
      }
      return id;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // 2. Fetch Device Data & Heartbeat
  const { data: device, isLoading: isDeviceLoading } = useQuery({
    queryKey: ['device', deviceId],
    enabled: !!deviceId,
    initialData: () => deviceId ? getInitialDeviceFromCache(deviceId) : undefined,
    // Prefer initial data if available, but verify with server
    // However, if we want offline support for prefs, we should respect local cache?
    // The previous implementation used DB as source of truth for prefs too, but initialized with cache
    // Let's set staleTime to 5 mins so we don't hammer the server
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!deviceId) throw new Error('No device ID');

      // Try direct table access first (works for authenticated users)
      let data: any = null;
      let fetchError: any = null;

      const directResult = await supabase
        .from('devices')
        .select(DEVICE_COLUMNS)
        .eq('id', deviceId)
        .single();

      if (directResult.error) {
        // Fallback to RPC for anonymous/unauthenticated access
        const rpcResult = await supabase.rpc('get_device_by_id', { _device_id: deviceId });
        if (rpcResult.error || !rpcResult.data) {
          console.error('Error fetching device:', directResult.error);
          throw directResult.error;
        }
        data = rpcResult.data;
      } else {
        data = directResult.data;
      }

      // Heartbeat: update last seen (no IP tracking for privacy)
      await supabase
        .from('devices')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('id', deviceId);

      const nextDevice = mapToDevice(data);

      // Update local cache sync
      saveDevicePrefsCache(nextDevice);

      return nextDevice;
    },
  });

  // 3. Fetch All Devices
  const { data: devices } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('devices')
        .select(DEVICE_COLUMNS)
        .order('last_seen_at', { ascending: false });

      if (error) {
        console.error('Error fetching devices:', error);
        throw error;
      }

      return data.map(mapToDevice);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (was 1 min)
  });

  // 4. Update Device Mutation
  const updateDeviceMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Device> }) => {
      const dbUpdates: any = {};

      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.defaultView !== undefined) dbUpdates.default_view = updates.defaultView;
      if (updates.isTvMode !== undefined) dbUpdates.is_tv_mode = updates.isTvMode;
      if (updates.askWhoCompleted !== undefined) dbUpdates.ask_who_completed = updates.askWhoCompleted;
      if (updates.showCompleted !== undefined) dbUpdates.show_completed = updates.showCompleted;
      if (updates.showSnoozed !== undefined) dbUpdates.show_snoozed = updates.showSnoozed;
      if (updates.filterPersonIds !== undefined) dbUpdates.filter_person_ids = updates.filterPersonIds;
      if (updates.filterCategory !== undefined) dbUpdates.filter_category = updates.filterCategory;
      if (updates.tvViewMode !== undefined) dbUpdates.tv_view_mode = updates.tvViewMode;
      if (updates.enableAnimations !== undefined) dbUpdates.enable_animations = updates.enableAnimations;
      if (updates.animationStyle !== undefined) dbUpdates.animation_style = updates.animationStyle;
      if (updates.timeFilterDays !== undefined) dbUpdates.time_filter_days = updates.timeFilterDays;

      const { error } = await supabase
        .from('devices')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;
      return { id, updates };
    },
    onMutate: async ({ id, updates }) => {
      // Optimistic Update
      await queryClient.cancelQueries({ queryKey: ['device', id] });
      await queryClient.cancelQueries({ queryKey: ['devices'] });

      const previousDevice = queryClient.getQueryData<Device>(['device', id]);

      if (previousDevice) {
        const nextDevice = { ...previousDevice, ...updates };
        queryClient.setQueryData(['device', id], nextDevice);
        // Also update local cache for immediate consistency if we reload
        saveDevicePrefsCache(nextDevice);
      }

      return { previousDevice };
    },
    onError: (err, variables, context) => {
      if (context?.previousDevice) {
        queryClient.setQueryData(['device', variables.id], context.previousDevice);
      }
      console.error('Error updating device:', err);
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['device', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  // 5. Delete Device Mutation
  const deleteDeviceMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('devices')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const updateDevice = useCallback(async (id: string, updates: Partial<Device>) => {
    try {
      await updateDeviceMutation.mutateAsync({ id, updates });
      return true;
    } catch {
      return false;
    }
  }, [updateDeviceMutation]);

  const deleteDevice = useCallback(async (id: string) => {
    try {
      await deleteDeviceMutation.mutateAsync(id);
      return true;
    } catch {
      return false;
    }
  }, [deleteDeviceMutation]);

  // Wrapper for manual fetch to match interface, though typically not needed with React Query
  const fetchDevices = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['devices'] });
  }, [queryClient]);

  return {
    device: device ?? null,
    deviceId: deviceId ?? null,
    devices: devices ?? [],
    loading: isDeviceIdLoading || isDeviceLoading,
    fetchDevices,
    updateDevice,
    deleteDevice,
  };
}
